import type { ColumnLayout, TableKey } from "@unbox/api";

export const MIN_COLUMNS = 4;
export const MAX_COLUMNS = 12;

export const MAX_COLUMNS_WARNING = `You can show up to ${MAX_COLUMNS} columns. Hide one to add another.`;
export const MIN_COLUMNS_WARNING = `Your table needs at least ${MIN_COLUMNS} columns.`;

export type ColumnOption = {
  id: string;
  group: string;
  lock?: "pinned" | "unhideable";
};

export type ColumnCatalog = {
  tableKey: TableKey;
  options: ColumnOption[];
  defaults: string[];
  groups: string[];
};

export type LayoutChange = { layout: ColumnLayout } | { warning: string };

const dedupe = (ids: string[]) => [...new Set(ids)];

const pinnedIds = (catalog: ColumnCatalog) => catalog.options.filter((o) => o.lock === "pinned").map((o) => o.id);

const lockedIds = (catalog: ColumnCatalog) => new Set(catalog.options.filter((o) => o.lock).map((o) => o.id));

export const visibleIds = (layout: ColumnLayout) => {
  const hidden = new Set(layout.hidden);
  return layout.order.filter((id) => !hidden.has(id));
};

export const columnCount = (layout: ColumnLayout) => visibleIds(layout).length + 1;

export const defaultLayout = (catalog: ColumnCatalog): ColumnLayout => {
  const ids = catalog.options.map((o) => o.id);
  const defaults = new Set(catalog.defaults);
  const locked = lockedIds(catalog);
  return {
    order: dedupe([...pinnedIds(catalog), ...catalog.defaults, ...ids]).filter((id) => ids.includes(id)),
    hidden: ids.filter((id) => !defaults.has(id) && !locked.has(id)),
  };
};

export const normalize = (catalog: ColumnCatalog, saved: ColumnLayout | undefined): ColumnLayout => {
  if (!saved) return defaultLayout(catalog);

  const ids = catalog.options.map((o) => o.id);
  const known = new Set(ids);
  const locked = lockedIds(catalog);
  const savedIds = new Set(saved.order);

  const order = dedupe([...pinnedIds(catalog), ...saved.order, ...ids]).filter((id) => known.has(id));
  const hidden = new Set(
    [...saved.hidden, ...ids.filter((id) => !savedIds.has(id))].filter((id) => known.has(id) && !locked.has(id)),
  );

  const visible = () => order.filter((id) => !hidden.has(id));
  while (visible().length + 1 > MAX_COLUMNS) {
    const last = visible()
      .reverse()
      .find((id) => !locked.has(id));
    if (!last) break;
    hidden.add(last);
  }
  for (const id of order) {
    if (visible().length + 1 >= MIN_COLUMNS) break;
    hidden.delete(id);
  }

  return { order, hidden: order.filter((id) => hidden.has(id)) };
};

export const showColumn = (layout: ColumnLayout, id: string): LayoutChange => {
  if (columnCount(layout) >= MAX_COLUMNS) return { warning: MAX_COLUMNS_WARNING };
  return {
    layout: {
      order: [...layout.order.filter((other) => other !== id), id],
      hidden: layout.hidden.filter((other) => other !== id),
    },
  };
};

export const hideColumn = (catalog: ColumnCatalog, layout: ColumnLayout, id: string): LayoutChange => {
  if (lockedIds(catalog).has(id)) return { layout };
  if (columnCount(layout) <= MIN_COLUMNS) return { warning: MIN_COLUMNS_WARNING };
  return { layout: { order: layout.order, hidden: [...layout.hidden, id] } };
};

export const moveColumn = (catalog: ColumnCatalog, layout: ColumnLayout, activeId: string, overId: string) => {
  const pinned = pinnedIds(catalog);
  const movable = visibleIds(layout).filter((id) => !pinned.includes(id));
  const from = movable.indexOf(activeId);
  const to = movable.indexOf(overId);
  if (from === -1 || to === -1 || from === to) return layout;

  const moved = [...movable];
  moved.splice(to, 0, ...moved.splice(from, 1));
  return { order: [...pinned, ...moved, ...layout.hidden], hidden: layout.hidden };
};
